import { useEffect } from "react"
import { AdminTournamentClient } from "@/domain/admin/client/AdminTournamentClient"
import {
  AdminTournamentListStore,
  makeInitAdminTournamentListState,
} from "@/domain/admin/store/AdminTournamentListStore"
import { PAGE_SIZE } from "@/config/AppConfig"

/**
 * 관리자 대회 목록 훅.
 *
 * 목록은 관리자 API 라 취소된 대회도 보이고 신청 건수가 함께 내려온다.
 */
export function useAdminTournamentList() {
  function useAdminTournamentListState() {
    return AdminTournamentListStore.useStore()
  }

  async function fetchList(page: number) {
    const state = AdminTournamentListStore.store.getState()
    AdminTournamentListStore.set("isLoaded", false)

    try {
      const result = await AdminTournamentClient.getByPaging({
        name: state.keyword,
        status: state.status,
        skip: (page - 1) * PAGE_SIZE,
        take: PAGE_SIZE,
      })
      AdminTournamentListStore.set("tournaments", result.items)
      AdminTournamentListStore.set("totalCount", result.totalCount)
      AdminTournamentListStore.set("currentPage", page)
      AdminTournamentListStore.set("errorMessage", "")
    } catch (error) {
      AdminTournamentListStore.set("tournaments", [])
      AdminTournamentListStore.set("totalCount", 0)
      AdminTournamentListStore.set(
        "errorMessage",
        error instanceof Error
          ? error.message
          : "대회 목록을 불러오지 못했습니다.",
      )
    } finally {
      AdminTournamentListStore.set("isLoaded", true)
    }
  }

  function setKeyword(keyword: string) {
    AdminTournamentListStore.set("keyword", keyword)
  }

  function setStatus(status: string) {
    AdminTournamentListStore.set("status", status)
  }

  async function search() {
    await fetchList(1)
  }

  async function changePage(page: number) {
    await fetchList(page)
  }

  async function remove(id: string) {
    try {
      await AdminTournamentClient.remove(id)
      const state = AdminTournamentListStore.store.getState()
      await fetchList(state.currentPage)
    } catch (error) {
      AdminTournamentListStore.set(
        "errorMessage",
        error instanceof Error ? error.message : "대회 삭제에 실패했습니다.",
      )
    }
  }

  useEffect(() => {
    AdminTournamentListStore.store.setState(makeInitAdminTournamentListState())
    void fetchList(1)

    return () => {
      AdminTournamentListStore.store.setState(
        makeInitAdminTournamentListState(),
      )
    }
  }, [])

  return {
    useAdminTournamentListState,
    setKeyword,
    setStatus,
    search,
    changePage,
    remove,
  }
}
